import * as _ from 'lodash'

import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, IIdentityProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_GeneralLedgerPolicyAccountRow, WB_SpendTypeRow, WB_TPM_EventSpendTypeAccountRow,
    WB_TPM_EventSpendTypeRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbTPMEventSpendType} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_TPM_Event_Spend,
    esWB_Event_Spend_Type,
    esWB_General_Ledger_Policy_Account,
    esWB_Event_Spend_Type_Account,
    esWB_Spend_Type,
    esWB_Source
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {EventSpendTypeBOValidations} from "./EventSpendTypeBOValidations";
import {EventSpendTypeBOAudit} from "./EventSpendTypeBO.Audit";
import {AuditService} from "../PA.Core/Security/AuditService";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {throwError} from "rxjs";


export interface IEventSpendTypeLoadOptions {
    dbContext?: PAdbContext
}

const EventSpendTypeLoadOptions_default: IEventSpendTypeLoadOptions = {}

export class EventSpendTypeBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_EventSpendType: WB_TPM_EventSpendTypeRow;

    dctAuditedDeletes: { [id: number]: number }

    WB_EventSpendType_Old: WB_TPM_EventSpendTypeRow

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;
        else
            this.dbContext = new PAdbContext()

        this.Identity = new IdentityProvider({id: 0, name: 'New EventType'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_EventSpendType = undefined;
        this.dctAuditedDeletes = {}
        this.WB_EventSpendType_Old = undefined;
    }

    //</editor-fold>

    get WB_EventSpendType(): WB_TPM_EventSpendTypeRow {
        return this.drWB_EventSpendType;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Event Spend Type");

        let internalOnComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == undefined) {
                let sError = "Unable to determine dbContext for new EventType.";
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Event_Spend_Type)
                .then((newEventTypeRow: WB_TPM_EventSpendTypeRow) => {
                    newEventTypeRow.setDefaults()
                    self.drWB_EventSpendType = newEventTypeRow;
                    self.dbContext.add(esWB_Event_Spend_Type, newEventTypeRow)
                    self.Identity.setAll(self.WB_EventSpendType.cif.WB_EVTSTYPE_ID, self.WB_EventSpendType.cif.SPENDTYPE_ID, self.WB_EventSpendType.cif.SPEND_TYPE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_EventSpendType, 'SPENDTYPE_ID', 'TYPE_ID', 'SPEND_TYPE_NAME')

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        self._OnLoadComplete = internalOnComplete

        return new Promise<IBusinessObject>((resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve()
                }).then((ignored) => {
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.finish()
                resolve(self)
            }).catch((err) => {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Event SpendType already loaded.")
                resolve(self); // "Event Spend Type already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, EventSpendTypeLoadOptions_default)


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isUndefined(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isUndefined(self.dbContext)) {
                error("Unable to determine dbContext for Event Type.")
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = EventSpendTypeBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = EventSpendTypeBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let aEventSpendTypeRow: any = recordResolver.get(esWB_Event_Spend_Type, anId)

                    if (_.isEmpty(aEventSpendTypeRow)) {
                        error("Event Spend Type not Found");
                        return;
                    }

                    self.drWB_EventSpendType = aEventSpendTypeRow;
                    self.WB_EventSpendType_Old = _.clone(aEventSpendTypeRow)
                    self.Identity.setAll(self.WB_EventSpendType.cif.WB_EVTSTYPE_ID, self.WB_EventSpendType.cif.SPENDTYPE_ID, self.WB_EventSpendType.cif.SPEND_TYPE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_EventSpendType, 'WB_EVTSTYPE_ID', 'SPENDTYPE_ID', 'SPEND_TYPE_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return;
            });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    //------------EVENT SPEND ACCOUNT---------------------------------------------------
//<editor-fold desc = "EVENT SPEND TYPE Account">
    addGLRLAccount(): Promise<IBusinessObject> {

        let self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            let aGLRLAccountRow: WB_TPM_EventSpendTypeAccountRow = undefined

            self.dbContext.createRecordWithKey(esWB_Event_Spend_Type_Account)
                .then((aNewRow: WB_TPM_EventSpendTypeAccountRow) => {
                    aNewRow.setDefaults()
                    aGLRLAccountRow = aNewRow
                    aGLRLAccountRow.cif.WB_EVTSTYPE_ID = self.drWB_EventSpendType.cif.WB_EVTSTYPE_ID
                    self.dbContext.add(esWB_Event_Spend_Type_Account, aGLRLAccountRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeGLRLAccount(aGLRLAccountId: number): Promise<IBusinessObject> {
        let self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_EventSpendType.WB_EventSpendAccounts, (aGLRLAccountRow: WB_TPM_EventSpendTypeAccountRow) => {
                if (aGLRLAccountRow.isActive && aGLRLAccountRow.cif.WB_EVTSACCT_ID == aGLRLAccountId) {
                    aGLRLAccountRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

//</editor-fold>


    Validate(options?: any): Promise<IBusinessObject> {

        let self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_EventSpendType.cif.SPEND_TYPE_NAME + "(" + self.WB_EventSpendType.cif.SPENDTYPE_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                let sError = "Unable to determine dbContext for to Validate Event Spend Type.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
                return
            }

            EventSpendTypeBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);
                })
        });

    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        const wbEventSpendType = <IwbTPMEventSpendType>self.drWB_EventSpendType
        self.SaveProgressProvider.start("Saving " + wbEventSpendType.SPEND_TYPE_NAME + "(" + wbEventSpendType.SPENDTYPE_ID + ")");


        // Doesn't need to do anything yet
        // All the records are in the store, since they were added through find or createNewRecord.
        // -- We could search the tree if the there is any record where store isn't set.
        try {
            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Event Spend Type.";
                self.SaveProgressProvider.exception(sError);
                return self
            }

            self.SaveOptions = options || {};
            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }
            let bNew: boolean = false
            if (self.WB_EventSpendType.isActiveNew)
                bNew = true

            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            const tasks: Promise<any>[] = []
            if (self.WB_EventSpendType.cif.PEA_IND != '1') {
                self.WB_EventSpendType.cif.TRADEIND = '0'
                self.WB_EventSpendType.cif.CONSUMERDISCOUNTIND = '0'
                self.WB_EventSpendType.cif.RETAILERMARGININD = '0'
            }

            if (!self.WB_EventSpendType.cif.RATEUOM_OPTIONS.includes("PCT"))
                self.WB_EventSpendType.cif.RATE_ROUNDING_POLICY=""

            let auditService = await EventSpendTypeBOAudit.EventSpendTypeAuditBO(self)

            tasks.push(saveFunction(esWB_Event_Spend_Type, self.drWB_EventSpendType))
            tasks.push(saveFunction(esWB_Event_Spend_Type_Account, self.drWB_EventSpendType.WB_EventSpendAccounts))
            await Promise.all(tasks)

            if (auditService)
                await auditService.Save(bBatch)

            let spendM: WB_SpendTypeRow = undefined

            if (!bNew && (self.WB_EventSpendType.cif.PEA_IND == '1' || (!_.isEmpty(self.WB_EventSpendType_Old) && self.WB_EventSpendType_Old.cif.PEA_IND == "1" && self.WB_EventSpendType.cif.PEA_IND == '0')))
                spendM = <WB_SpendTypeRow>await self.dbContext.find(esWB_Spend_Type, self.WB_EventSpendType.cif.WB_EVTSTYPE_ID)

            if (bNew && self.WB_EventSpendType.cif.PEA_IND == '1') {
                spendM = new WB_SpendTypeRow(undefined, undefined)
                spendM.cif.WB_SPENDM_ID = self.WB_EventSpendType.cif.WB_EVTSTYPE_ID
                spendM.cif.SPEND_TYPE_ID = self.WB_EventSpendType.cif.SPEND_TYPE_NAME
                spendM.cif.SPEND_TYPE_NAME = self.WB_EventSpendType.cif.SPEND_TYPE_NAME
                spendM.cif.WB_MDSET_ID = self.WB_EventSpendType.cif.WB_MDSET_ID
                spendM.cif.WB_SALESORG_ID = self.WB_EventSpendType.cif.WB_SALESORG_ID
            }
            if (!_.isEmpty(spendM)) {
                spendM.cif.SPEND_TYPE_ID = self.WB_EventSpendType.cif.SPEND_TYPE_NAME
                spendM.cif.SPEND_TYPE_NAME = self.WB_EventSpendType.cif.SPEND_TYPE_NAME
                spendM.cif.TRADEIND = self.WB_EventSpendType.cif.TRADEIND
                spendM.cif.RETAILERMARGININD = self.WB_EventSpendType.cif.RETAILERMARGININD
                spendM.cif.CONSUMERDISCOUNTIND = self.WB_EventSpendType.cif.CONSUMERDISCOUNTIND
                spendM.cif.SOURCE_ID = self.WB_EventSpendType.cif.SOURCE_ID
                spendM.cif.WB_SOURCE_ID = (await self.dbContext.findAll(esWB_Source, {where: {SOURCE_ID: spendM.cif.SOURCE_ID, WB_MDSET_ID: spendM.cif.WB_MDSET_ID, DELETED_IND:{neq:'1'}}}))[0].WB_SOURCE_ID
                await saveFunction(esWB_Spend_Type, spendM)
            }

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }
            self.WB_EventSpendType_Old = _.clone(self.drWB_EventSpendType)
            self.SaveProgressProvider.finish();
            return self
        } catch (err) {
            self.SaveProgressProvider.exception(err);
            return self
        }

    }


    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        let self = this;
        let wbEventSpendType = <IwbTPMEventSpendType>self.drWB_EventSpendType;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbEventSpendType.SPEND_TYPE_NAME + "(" + wbEventSpendType.SPENDTYPE_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the EventType

            // 2. If Valid, mark all the records for deletion.
            self.drWB_EventSpendType.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        return EventSpendTypeBOAudit.DeleteEventSpendType(self);
                    })
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

//</editor-fold>


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        }

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Event_Spend_Type, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }


};
PAEnvironment.registerBusinessObjectClass(EventSpendTypeBO, "EventSpendTypeBO", "Event Spend Type")
